import { useEffect, useRef, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { cn } from 'src/helpers/style'

import { AppIcon, AppIconNames } from 'src/components/atoms'

export interface ISearchableFilterDropdownOption {
  value: string
  label: string
}

export interface ISearchableFilterDropdownProps {
  value: string
  onChange: (value: string) => void
  options: ISearchableFilterDropdownOption[]
  placeholder?: string
  defaultValue?: string
  name?: string
  className?: string
}

export const SearchableFilterDropdown = ({
  value,
  onChange,
  options,
  placeholder = '',
  defaultValue,
  name = 'searchable-filter-dropdown',
  className,
}: ISearchableFilterDropdownProps): JSX.Element => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const selectedOption = options.find(o => o.value === value)
  const displayValue = selectedOption?.label ?? ''

  const filteredOptions = searchTerm
    ? options.filter(o =>
        o.label.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : options

  const open = (): void => {
    setIsOpen(true)
    setSearchTerm('')
    requestAnimationFrame(() => inputRef.current?.focus())
  }

  const close = (): void => {
    setIsOpen(false)
    setSearchTerm('')
  }

  const handleSelect = (optionValue: string): void => {
    onChange(optionValue)
    close()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Escape') close()
  }

  useEffect(() => {
    const handler = (e: MouseEvent): void => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        close()
      }
    }
    document.addEventListener('mousedown', handler)
    return (): void => document.removeEventListener('mousedown', handler)
  }, [])

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div
        className={cn(
          'flex h-[44px] w-full items-center justify-between rounded-full bg-surface-raised px-5',
          isOpen
            ? 'border border-content-secondary'
            : 'border border-border-default',
        )}
      >
        <input
          ref={inputRef}
          role="combobox"
          id={name}
          type="text"
          autoComplete="off"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${name}-listbox`}
          aria-label={placeholder || name}
          value={isOpen ? searchTerm : displayValue}
          readOnly={!isOpen}
          placeholder={isOpen ? displayValue || placeholder : placeholder}
          className={cn(
            'w-full bg-transparent text-[13px] leading-[1.55] outline-none placeholder:text-content-tertiary',
            !isOpen && 'cursor-pointer',
            !isOpen && defaultValue !== undefined && value === defaultValue
              ? 'text-content-tertiary'
              : 'text-content-primary',
          )}
          onChange={e => setSearchTerm(e.target.value)}
          onClick={() => {
            if (!isOpen) open()
          }}
          onKeyDown={handleKeyDown}
        />
        <button
          type="button"
          tabIndex={-1}
          aria-label={isOpen ? 'Close filter' : 'Open filter'}
          className="ml-2 shrink-0"
          onClick={() => (isOpen ? close() : open())}
        >
          <AppIcon
            name={AppIconNames.chevronDown}
            className={cn(
              'size-4 text-content-primary transition-transform duration-200',
              isOpen && 'rotate-180',
            )}
          />
        </button>
      </div>

      {isOpen && (
        <ul
          id={`${name}-listbox`}
          role="listbox"
          className="absolute right-0 top-[calc(100%+4px)] z-10 max-h-[410px] min-w-[200px] overflow-y-auto rounded-[8px] bg-surface-raised shadow-[0px_4px_8px_rgba(44,42,36,0.1)]"
        >
          {filteredOptions.length === 0 ? (
            <li className="px-4 py-3 text-body-xs text-content-secondary">
              {t('portfolioNews.filters.noResultsFound')}
            </li>
          ) : (
            filteredOptions.map(option => (
              <li
                key={option.value}
                role="option"
                aria-selected={option.value === value}
                className={cn(
                  'cursor-pointer whitespace-nowrap px-4 py-3 text-body-xs text-content-secondary',
                  option.value === value
                    ? 'bg-border-strong font-medium'
                    : 'bg-surface-raised hover:bg-state-hover-fill',
                )}
                onMouseDown={e => e.preventDefault()}
                onClick={() => handleSelect(option.value)}
              >
                {option.label}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  )
}
